<template>
  <div class="flex min-w-full flex-col">
    <!-- Tabs -->
    <div class="tl-tabs">
      <button
        v-for="(tab, index) in tabs"
        :key="index"
        type="button"
        :class="{
          'active-tab': activeTab?.contentLink.expanded.tabId === tab.contentLink.expanded.tabId,
          'default-tab': activeTab?.contentLink.expanded.tabId !== tab.contentLink.expanded.tabId,
        }"
        class="whitespace-nowrap p-4 md:py-2"
        @click="selectTab(tab)"
      >
        <span
          :data-title="tab.contentLink.expanded.name.replace('Tab: ', '')"
          class="before:block before:h-0 before:font-bold before:opacity-0 before:content-[attr(data-title)]"
          >{{ tab.contentLink.expanded.name.replace("Tab: ", "") }}</span
        >
      </button>
    </div>

    <!-- Tab Content -->
    <div v-if="activeTab?.contentLink.expanded.tabId">
      <slot :name="activeTab.contentLink.expanded.tabId"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, defineEmits, onMounted } from "vue";
import type { ITab } from "../types";

const emit = defineEmits(["tabSelected", "mounted"]);
const props = defineProps<IProps>();
// Define props to pass the tabs array
export interface IProps {
  tabs: ITab[];
}

// Set the initial active tab to the first tab in the props array
const activeTab = ref<ITab | null>(props.tabs[0]);

// Function to handle tab selection and update the active tab
const selectTab = (tab: ITab) => {
  activeTab.value = tab;
  emit("tabSelected", tab); // Emit the tabSelected event with the selected tab
};

// Watch for changes in the tabs prop and update the active tab accordingly
watch(
  () => props.tabs,
  (newTabs) => {
    if (newTabs.length > 0) {
      activeTab.value = newTabs[0];
    }
  },
);

// Emit mounted event when the component is mounted
onMounted(() => {
  emit("mounted");
});
</script>

<style lang="scss">
.tl-tabs {
  display: flex;
  @apply md:pb-0.5 max-w-full overflow-x-auto;

  @-moz-document url-prefix() {
    scrollbar-width: thin;
  }
  &::-webkit-scrollbar {
    @apply h-2 rounded-xl bg-[color:var(--color-neutral-a6)] border-none;
  }
  &::-webkit-scrollbar-thumb {
    @apply bg-[color:var(--color-neutral-a4)] rounded-xl;
  }
  &::-webkit-scrollbar-track {
    @apply rounded-xl;
  }

  button {
    flex: 1 1 0;
  }
}
.default-tab {
  @apply border-b-2 border-[--color-neutral-a4] font-medium hover:border-b-[--color-primary-a1];
}
.active-tab {
  @apply border-b-2 border-b-[--color-primary-a1] font-medium text-[--color-neutral-a1];
}
</style>
